import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import StyleIcon from '@mui/icons-material/Style';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShieldIcon from '@mui/icons-material/Shield';
import SearchIcon from '@mui/icons-material/Search';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import BurstModeIcon from '@mui/icons-material/BurstMode';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import config from '../config.json';

const { cardfolio, urls } = config;

const highlightIcons = [<CameraAltIcon />, <SmartToyIcon />, <ShieldIcon />, <SearchIcon />];
const highlights = cardfolio.highlights.items.map((item, i) => ({
  ...item,
  icon: highlightIcons[i],
}));

const featureIcons = [
  <CollectionsBookmarkIcon />, <TrendingUpIcon />, <DashboardCustomizeIcon />,
  <ViewModuleIcon />, <BurstModeIcon />, <OfflineBoltIcon />,
];
const features = cardfolio.features.items.map((item, i) => ({
  ...item,
  icon: featureIcons[i],
}));

const platformIconMap = { Apple: <AppleIcon sx={{ fontSize: 16 }} />, Android: <AndroidIcon sx={{ fontSize: 16 }} /> };
const ctaPlatformIconMap = { Apple: <AppleIcon />, Android: <AndroidIcon /> };

function CardfolioPage() {
  const theme = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-20%',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: theme.custom.heroGlow1,
            filter: 'blur(60px)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: theme.custom.heroGlow2,
            filter: 'blur(60px)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: 'text.secondary',
              mb: 4,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: theme.custom.hoverBg,
              },
            }}
          >
            {cardfolio.backButton}
          </Button>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 8 },
            }}
          >
            {/* App Mockup */}
            <Box
              sx={{
                flex: '0 0 auto',
                width: { xs: 220, md: 280 },
                height: { xs: 440, md: 560 },
                borderRadius: 2,
                background: theme.custom.mockupBg,
                border: `3px solid ${theme.custom.mockupBorder}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: theme.custom.mockupShadow,
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-12px)' },
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 30,
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '24px 24px 0 0',
                  zIndex: 2,
                }}
              />
              <Box
                component="img"
                src={urls.dashboardImage}
                alt="Cardfolio Dashboard"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'top center',
                }}
              />
            </Box>

            {/* Product Info */}
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {cardfolio.platforms.map((platform) => (
                  <Chip
                    key={platform.label}
                    icon={platformIconMap[platform.icon]}
                    label={platform.label}
                    size="small"
                    sx={{
                      bgcolor: theme.custom.chipBg,
                      color: 'primary.light',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Stack>

              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <PhoneIphoneIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 36 }}
                />
                <Typography variant="h1">
                  <Box
                    component="span"
                    sx={{
                      background: theme.custom.gradientPrimary,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {cardfolio.productName}
                  </Box>
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', mb: 2, fontSize: '1.15rem' }}
              >
                {cardfolio.heroDescription1}
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', mb: 4, fontSize: '1.15rem' }}
              >
                {cardfolio.heroDescription2}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ px: 4, py: 1.5 }}
                  onClick={() => window.open(urls.appStoreLink, '_blank')}
                >
                  {cardfolio.downloadButton}
                </Button>
                <Button
                  component={RouterLink}
                  to="/cardfolio/user-guide"
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<HelpOutlineIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 },
                  }}
                >
                  {cardfolio.userGuideButton}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Highlights Section */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {cardfolio.highlights.headingPrefix}
              <Box
                component="span"
                sx={{
                  background: theme.custom.gradientPrimary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {cardfolio.highlights.headingAccent}
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}
            >
              {cardfolio.highlights.description}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr 1fr',
              },
              gap: 3,
            }}
          >
            {highlights.map((item, index) => (
              <Card
                key={index}
                elevation={0}
                sx={{
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: 'primary.main',
                    boxShadow: theme.custom.shadow,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      mx: 'auto',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: theme.custom.highlightGradient,
                      color: 'primary.main',
                      '& .MuiSvgIcon-root': { fontSize: 28 },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: theme.custom.sectionTint,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 3,
                fontSize: '0.85rem',
              }}
            >
              {cardfolio.howItWorks.overline}
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, mb: 2 }}>
              {cardfolio.howItWorks.heading}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              {cardfolio.howItWorks.description}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr 1fr',
              },
              gap: 3,
            }}
          >
            {cardfolio.howItWorks.steps.map((item) => (
              <Card key={item.step} elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      mx: 'auto',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: theme.custom.gradientPrimary,
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: '1.2rem',
                    }}
                  >
                    {item.step}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 3,
                fontSize: '0.85rem',
              }}
            >
              {cardfolio.features.overline}
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, mb: 2 }}>
              {cardfolio.features.heading}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              {cardfolio.features.description}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr',
              },
              gap: 3,
            }}
          >
            {features.map((feature, index) => (
              <Card
                key={index}
                elevation={0}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: 'primary.main',
                    boxShadow: theme.custom.shadowLg,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      mb: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: theme.custom.featureGradients[index],
                      color: '#fff',
                      '& .MuiSvgIcon-root': { fontSize: 26 },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1.5 }}>
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Privacy Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: theme.custom.sectionTint,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <ShieldIcon
              sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}
            />
            <Typography variant="h2" sx={{ mb: 2 }}>
              {cardfolio.privacy.heading}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', mb: 4 }}
            >
              {cardfolio.privacy.description}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3,
            }}
          >
            {cardfolio.privacy.items.map((item, i) => (
              <Card key={i} elevation={0}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="sm">
          <Card
            elevation={0}
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              background: theme.custom.productCardBg,
              border: `1px solid ${theme.custom.cardBorder}`,
              borderRadius: 4,
            }}
          >
            <StyleIcon
              sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}
            />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {cardfolio.cta.heading}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', mb: 4 }}
            >
              {cardfolio.cta.description}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AppleIcon />}
                sx={{ px: 4 }}
                onClick={() => window.open(urls.appStoreLink, '_blank')}
              >
                {cardfolio.cta.appStoreButton}
              </Button>
              {/* <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AndroidIcon />}
                sx={{ px: 4 }}
              >
                {cardfolio.cta.googlePlayButton}
              </Button> */}
            </Stack>
          </Card>
        </Container>
      </Box>

      <Footer />
    </>
  );
}

export default CardfolioPage;
